'use client';

import type { ReactNode } from 'react';
import React, { cloneElement } from 'react';
import { isFunction } from 'utils/is-function';
import { mergeProps, mergeRefs } from '@react-aria/utils';
import { theme } from '@frameio/vapor-theme';
import { useFilteredDataAttributes } from '../../hooks/useFilteredDataAttributes';
import type { TooltipContentProps } from './TooltipContent';
import { TooltipContent } from './TooltipContent';
import type { TargetHoverProps, UseTooltipProps } from './useTooltip';
import { useTooltip } from './useTooltip';

export type TargetAttributes = {
  bind: TargetHoverProps;
  isShowing: boolean;
};

export type PopoverTargetRenderCallback = (
  props: TargetAttributes
) => ReactNode;

export type PopoverTargetChildren = ReactNode | PopoverTargetRenderCallback;

export type TooltipProps = {
  /** Whether the Tooltip should render, useful for deferring mount while parent is loading. */
  shouldRender?: boolean;
  /** Optional className which styles the Popover content container. */
  className?: string;
  /** The target element which triggers the tooltip to show. */
  children?: PopoverTargetChildren;
  /**
   * Optionally provide an anchor which is distinct from the element which triggers
   * the Tooltip.
   */
  anchorRef?: any;
  /** Enable Tooltips on mobile touch devices. */
  enableOnMobile?: boolean;
  /** z-index for the Tooltip. */
  zIndex?: number;
} & Partial<UseTooltipProps> &
  Omit<Partial<TooltipContentProps>, 'children'>;

/**
 * Tooltip
 *
 * Handles displaying tooltips using absolute positioning,
 * triggered when the target is hovered.
 */

const defaultOffset = [0, 8] as const;

export function Tooltip({
  autoHide,
  boundary,
  isShowing: isShowingProp,
  onRequestChange,
  title,
  details,
  placement,
  defaultShowing,
  shouldRender = true,
  shouldUsePortal = true,
  maxWidth,
  delayIn = 600,
  offset = defaultOffset,
  className,
  children,
  anchorRef,
  disabled = false,
  enableOnMobile = false,
  adornmentBefore,
  adornmentAfter,
  zIndex = theme.zIndices.tooltip,
  observeContentSize,
  observeTargetSize,
  skipAriaDescription,
  ...otherProps
}: TooltipProps) {
  const { bindTarget, bindContent, isShowing } = useTooltip({
    isShowing: isShowingProp,
    onRequestChange,
    defaultShowing,
    delayIn,
    anchorRef,
    disabled,
    enableOnMobile,
    skipAriaDescription,
  });

  const [dataAttributes] = useFilteredDataAttributes(otherProps);

  const renderChildren = () => {
    if (!children) {
      return null;
    }

    // render callback
    if (isFunction(children)) {
      // @ts-expect-error FIXME(strictNullChecks)
      return children({ bind: bindTarget, isShowing });
    }

    // clone the child
    const child = React.Children.only(children) as any;

    const {
      ref,
      onMouseLeave,
      onMouseEnter,
      onMouseDown,
      onFocus,
      onBlur,
      ...otherBindAttributes
    } = bindTarget;

    const mergedRefs = mergeRefs(ref, child.props.ref ?? child.ref);

    return cloneElement(child, {
      ...otherBindAttributes,
      ...mergeProps(child.props, {
        ref: mergedRefs,
        onMouseEnter,
        onMouseLeave,
        onFocus,
        onBlur,
        onMouseDown,
      }),
    });
  };

  return (
    <>
      {shouldRender ? (
        <TooltipContent
          {...dataAttributes}
          {...bindContent}
          adornmentAfter={adornmentAfter}
          adornmentBefore={adornmentBefore}
          autoHide={autoHide}
          boundary={boundary}
          className={className}
          details={details}
          maxWidth={maxWidth}
          observeContentSize={observeContentSize}
          observeTargetSize={observeTargetSize}
          offset={offset}
          placement={placement}
          shouldUsePortal={shouldUsePortal}
          title={title}
          zIndex={zIndex}
        />
      ) : null}
      {(shouldRender ? renderChildren() : children) as ReactNode}
    </>
  );
}
